/**
 * The API client.
 *
 * Auth is httpOnly `Secure` `SameSite=Strict` cookies (design.md § 3), so every
 * request sets `credentials: 'include'` and the browser attaches the access and
 * refresh cookies itself. There is deliberately no token handling here at all:
 * nothing reads a token, nothing stores one, and NOTHING may ever put one in
 * localStorage — that is the whole point of the cookie design, because it means
 * an XSS cannot exfiltrate a session.
 *
 * Requests go to a relative /api path so the browser sees a single origin. In
 * development Vite proxies /api to 127.0.0.1:3002; in production nginx proxies
 * it to the api container. Cookies therefore behave identically in both.
 */

/**
 * What the API returns on a failure: `{ error: { code, message, details? } }`
 * (apps/api/src/lib/errors.ts). The envelope is nested. Reading `error` as a
 * string — as this once did — put an object into the message, which rendered as
 * "[object Object]" the first time a write was refused against the real API.
 */
import { isErrorCode, type ErrorCode } from '@blueprint/shared';

export interface ApiErrorBody {
  // `code` is typed against the shared union, so a typo here or in the API no
  // longer compiles on one side and silently never matches on the other
  // (T3.15a). It stays widened to string as well, because a body from a proxy,
  // a gateway or an older deployment is not guaranteed to be one of ours.
  error?: { code?: ErrorCode | (string & {}); message?: string; details?: unknown };
}

/** The most useful sentence a failed response offers, or a generic one. */
function messageFrom(status: number, body: ApiErrorBody | undefined): string {
  const message = body?.error?.message;
  return typeof message === 'string' && message ? message : `Request failed with status ${status}`;
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody | undefined;

  constructor(status: number, message: string, body?: ApiErrorBody) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }

  /** The API's machine-readable code (`conflict`, `version_published`, …), when it sent one. */
  get code(): string | undefined {
    return this.body?.error?.code;
  }

  /** The same code, but only when it is one this build knows about. */
  get knownCode(): ErrorCode | undefined {
    const c = this.body?.error?.code;
    return isErrorCode(c) ? c : undefined;
  }

  /**
   * 409 `version_published`: the version this write named was published by
   * somebody else while it was open. Distinct from a plain `conflict`, which
   * keeps the generic handling. The literal is checked against the shared union
   * at compile time, so a typo fails the build rather than quietly never
   * matching — which is what it would have done before T3.15a.
   */
  get isVersionPublished(): boolean {
    const published: ErrorCode = 'version_published';
    return this.status === 409 && this.code === published;
  }

  /** 401 means the session is gone or expired — the caller signs the user out. */
  get isUnauthorised(): boolean {
    return this.status === 401;
  }

  /** 403 means signed in but not permitted. A viewer must never see an editing control. */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

// The API mounts every route under this prefix (apps/api/src/app.ts, API_PREFIX).
// Gate G2 verification found this reading '/api', so every request 404'd against
// the real API while passing against a mock that answered without the /v1. If the
// API's prefix ever moves, test/api/web-api-contract.test.ts fails rather than the
// browser silently 404ing.
const BASE = '/api/v1';

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /** Serialised as JSON. Use `formData` instead for a workbook upload. */
  body?: unknown;
  formData?: FormData;
  signal?: AbortSignal;
}

/**
 * One place every call goes through, so the cookie and error handling cannot be
 * forgotten at a call site.
 */
/**
 * Paths that must never trigger a silent refresh (T7.23).
 *
 * `/auth/refresh` answering 401 is the terminal answer — retrying it would
 * recurse. `/auth/login` answering 401 is a wrong password, which is an answer
 * to show the user, not a dead session. `/auth/logout` ending a session that is
 * already gone is a success as far as the user is concerned.
 */
function isAuthPath(path: string): boolean {
  return path.startsWith('/auth/refresh') || path.startsWith('/auth/login') || path.startsWith('/auth/logout');
}

/**
 * The single in-flight refresh. THIS SHARING IS LOAD-BEARING, not an
 * optimisation.
 *
 * A screen fires several queries at once, so when the access cookie expires
 * several of them get a 401 within milliseconds of each other. The API rotates
 * refresh tokens and treats a SECOND use of an already-used token as theft: it
 * revokes the whole chain (apps/api/src/routes/auth.ts, "Refresh token has
 * already been used"). Letting each 401 refresh on its own would therefore sign
 * the user out precisely when the session was still perfectly valid — and it
 * would look intermittent, because it depends on how many queries a screen
 * happens to fire.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function postRefresh(): Promise<boolean> {
  try {
    const r = await fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'include',
    });
    return r.ok;
  } catch {
    // Offline or a dropped connection. Not a dead session, but there is
    // nothing to replay against either.
    return false;
  }
}

function refreshSession(): Promise<boolean> {
  refreshInFlight ??= postRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, formData, signal } = options;

  const headers: Record<string, string> = { Accept: 'application/json' };
  let payload: BodyInit | undefined;

  if (formData) {
    // Let the browser set Content-Type so it can add the multipart boundary.
    payload = formData;
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }

  const send = (): Promise<Response> =>
    fetch(`${BASE}${path}`, {
      method,
      headers,
      // The reason this module exists. Without it the session cookies are dropped.
      credentials: 'include',
      ...(payload !== undefined ? { body: payload } : {}),
      ...(signal ? { signal } : {}),
    });

  let response = await send();

  // The access cookie is short (15 minutes). Rather than signing the user out
  // when it expires, spend the rotating refresh cookie once and replay. ONE
  // attempt, never a chain: if the replay also 401s, the session really is
  // gone and the caller signs out.
  if (response.status === 401 && !isAuthPath(path) && (await refreshSession())) {
    response = await send();
  }

  if (!response.ok) {
    let parsed: ApiErrorBody | undefined;
    try {
      parsed = (await response.json()) as ApiErrorBody;
    } catch {
      // A proxy error or a dropped connection need not be JSON.
      parsed = undefined;
    }
    throw new ApiError(response.status, messageFrom(response.status, parsed), parsed);
  }

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) return undefined as T;

  return (await response.json()) as T;
}

/**
 * Multipart upload with progress. `fetch` cannot report upload progress, so this
 * is the one call that uses XMLHttpRequest. It keeps the same contract as
 * `request`: cookies are sent, a non-2xx becomes an `ApiError` carrying the parsed
 * body, and no token is handled anywhere.
 */
export function uploadWithProgress<T>(
  path: string,
  formData: FormData,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BASE}${path}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.responseType = 'text';

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onerror = () => reject(new ApiError(0, 'The upload could not reach the server.'));
    xhr.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'));
    xhr.onload = () => {
      let parsed: unknown;
      try {
        parsed = xhr.responseText ? JSON.parse(xhr.responseText) : undefined;
      } catch {
        parsed = undefined;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(parsed as T);
        return;
      }
      const body = parsed as ApiErrorBody | undefined;
      reject(new ApiError(xhr.status, messageFrom(xhr.status, body), body));
    };

    signal?.addEventListener('abort', () => xhr.abort());
    xhr.send(formData);
  });
}

/**
 * Fetch a binary response (the workbook export) and hand it to the browser as a
 * file. Done through `fetch` rather than a plain link so a refusal arrives as an
 * `ApiError` the screen can show, instead of the tab navigating to a JSON body.
 */
export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  const send = (): Promise<Response> => fetch(`${BASE}${path}`, { credentials: 'include' });

  let response = await send();

  // T7.43. This does not go through `request()`, so it did not get the silent
  // refresh with it: clicking Download after the 15-minute access cookie had
  // expired handed the browser a JSON 401 body instead of the file. It usually
  // self-healed, because a query on the same screen refreshes first — and
  // "usually" is exactly the kind of fault nobody reports. Same one attempt,
  // same shared in-flight promise, for the same reason: several 401s at once
  // must not each spend the rotating refresh token.
  if (response.status === 401 && (await refreshSession())) {
    response = await send();
  }

  if (!response.ok) {
    let parsed: ApiErrorBody | undefined;
    try {
      parsed = (await response.json()) as ApiErrorBody;
    } catch {
      parsed = undefined;
    }
    throw new ApiError(response.status, messageFrom(response.status, parsed), parsed);
  }

  const disposition = response.headers.get('content-disposition') ?? '';
  const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(disposition);
  const name = match ? decodeURIComponent(match[1] ?? match[2] ?? fallbackName) : fallbackName;

  const url = URL.createObjectURL(await response.blob());
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Revoking at once can cancel the download in some browsers.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) =>
    request<T>(path, signal ? { signal } : {}),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, body === undefined ? { method: 'POST' } : { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, body === undefined ? { method: 'PATCH' } : { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, formData: FormData) =>
    request<T>(path, { method: 'POST', formData }),
};
